import { BI_CONFIGURATION_FIELD, BI_LIMIT, biConfigurationOf, biDatasetChoices, biSelectedDatasets } from "@wetalk/domain";
import { portalText } from "../../../i18n/portal.js";
/**
 * The new-agent wizard's rules, as pure functions — customer-portal review fix round D
 * (2026-09-25; items #3–#24, #40, #41, #50, #126–#128).
 *
 * Everything the screen decides that is not drawing lives here, so it can be read and tested
 * without a browser: which step numbers exist and what the URL may say (#16, #22), what each step
 * still needs, in words, keyed by the control it belongs to (#5, #13, #127), which voices a set of
 * languages allows (#10, #13), the defaults that apply until the person answers — the account's
 * country for the language (#127), the tier for concurrency (#17), the business for the name (#21)
 * and the greeting (#11) — and the request the Build button sends.
 *
 * ONE NUMBERING (#22). The picker is step 1; the wizard is steps 2 to 5, and the URL's `?step=`
 * carries the same number the header and the step list show.
 *
 * AMENDED 2026-10-09 (design sync, unit B): the step list is per template — `wizardStepsOf` — so a template with an
 * order book asks Menu, Hours and Orders as steps 2 to 4 and reviews at 7; `?step=` runs 2 to 7. The voice is chosen
 * per language (`languageVoiceOf`), and the three payment choices, the capacity choices and the "Continue" hint are here
 * too. `problemOf` keeps the round-D step numbers for the Edit panel.
 *
 * AMENDED 2026-10-09 (plan-015 G1U, gap G1, ADR 0042): the build takes a voice PER LANGUAGE. `resolve` carries the
 * voice rows and the map the Build button sends as `voice_by_language`; the rule that every language speak in one
 * voice is gone. A language no voice speaks is still refused, by name.
 *
 * AMENDED 2026-10-09 (plan-015 AGC-UI, gaps G3, G4, G8, G12): "Send a checkout link by SMS" is `online` ALONE (owner
 * answer 2 — the API accepts it; while online payment cannot be offered the agent takes a message and the agent page
 * warns), and only exactly `["online"]` reads back as that choice. The answering answers — the VoiceAgent's own time
 * zone, its special dates and the out-of-hours and busy choices — are resolved by {@link answeringOf} and sent by
 * {@link buildRequestOf} as `answering` (IC-DG-2). They live on the answer under the optional keys merged below.
 *
 * AMENDED 2026-10-13 (plan-018 WAPP-A, design §30.12, ADR 0059, ED-31): three field-set facts, read by kind and never by a
 * template's code. A `website` field is a typed address checked by the api's own rule (`./information-input.ts`) and
 * sent in its stored form. An `upload` with `maxFileCount` takes several files: its value names them one per line (the
 * api's convention), and each file is kept under its own SLOT ({@link fileSlotOf} — the field code for a one-file
 * upload, as before). Fields sharing a `requiredGroup` are alternatives: none of them is required on its own, and the
 * group is refused, in one sentence naming them all, while none is given. The first upload is the required document only
 * when it belongs to no group.
 *
 * NO STEP AHEAD OF AN UNFINISHED ONE (#16). {@link firstIncompleteStep} is the first step with
 * something missing; the screen sends a URL for a later step back to it.
 */
import { missingRequiredGroupOf, type AgentTemplateField, type ConversationDirection } from "@wetalk/domain";

import type {
  AgentTemplateCardView,
  LanguagePackView,
  VoiceAgentDraft,
  VoiceView,
} from "../../../api/gateway.js";
import { amountProblem, fieldFormatOf } from "./field-format.js";
import { suggestedGreeting } from "./greeting.js";
import { isBetaLanguage } from "../../../shell/language-beta.js";
import {
  isOverTypedLimit,
  requiredGroupKeyOf,
  requiredGroupTextOf,
  typedTooLargeText,
  typedUploadCopyOf,
  websiteAddressSentOf,
  websiteProblemOf,
} from "./information-input.js";
import {
  DEFAULT_ANSWERING_CHOICE,
  answeringChoiceProblemOf,
  closedWeek,
  openingHoursText,
  openingWeekProblem,
  sentChoiceOf,
  type AnsweringChoice,
  type ForwardFact,
  type SpecialDateEntry,
} from "./opening-hours.js";
import type { CreatedInDraft, NumberChoice, PaymentMethodCode, WizardAnswer } from "./wizard-draft.js";
import type { AnsweringRequestView } from "../../../api/slice/voice-agent-edit.js";

/*
 * plan-015 AGC-UI: the answering answers ride on the wizard's answer. `wizard-draft.ts` is not this unit's, so they are
 * merged onto `WizardAnswer` here, optional: absent means "not answered yet" and {@link answeringOf}'s defaults apply.
 * The stored draft writes them with the rest (`saveDraft` stringifies the whole answer); reading them back after a
 * reload needs `loadDraft` to keep them (reported to the integrator).
 */
declare module "./wizard-draft.js" {
  interface WizardAnswer {
    /** The VoiceAgent's own time zone, once the person changes it; the workspace's until then. */
    readonly timeZone?: string;
    /** Dates closed or with their own hours. */
    readonly specialDate?: readonly SpecialDateEntry[];
    /** Out of hours, every line busy, the longest wait and the forward number. */
    readonly answeringChoice?: AnsweringChoice;
  }
}

// ── steps ──────────────────────────────────────────────────────────────────────────────────

export const CONTENT_STEP = 2;
export const LANGUAGE_STEP = 3;
export const MEDIUM_STEP = 4;
export const SUMMARY_STEP = 5;

/**
 * The highest `?step=` any template has: a template with an order book splits its content into Menu, Hours and Orders
 * (design sync 2026-10-09), so its Review is step 7. The screen narrows it to the template's own last step.
 */
export const MAX_WIZARD_STEP = 7;

/** The wizard route's `?step=`: 2 to 7, anything else is the first wizard step. */
export function wizardStepOf(raw: unknown): number {
  const asNumber = Number(raw);
  if (!Number.isFinite(asNumber)) return CONTENT_STEP;
  return Math.max(CONTENT_STEP, Math.min(MAX_WIZARD_STEP, Math.trunc(asNumber)));
}

const SHARE_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * plan-014 GAP: the `share` search parameter — the shared configuration a new agent starts from. Anything that is
 * not a share id is dropped (the wizard then starts from nothing), never guessed at.
 */
export function wizardShareOf(raw: unknown): string | null {
  return typeof raw === "string" && SHARE_SHAPE.test(raw) ? raw.toLowerCase() : null;
}

// ── the template's shape ───────────────────────────────────────────────────────────────────

/**
 * The reference schema whose build speaks one language for now — the factory's rule
 * (`apps/factory/src/build/draft-input.ts`), also refused by `POST /v1/agents`. The wizard offers a
 * single choice for it rather than letting a second language fail the build.
 */
const SINGLE_LANGUAGE_REFERENCE_SCHEMA = "pizzabot-menu-md-v1";

export function speaksOneLanguage(card: AgentTemplateCardView): boolean {
  return card.referenceSchema === SINGLE_LANGUAGE_REFERENCE_SCHEMA;
}

export function orderBookFieldOf(card: AgentTemplateCardView): AgentTemplateField | undefined {
  return card.fieldSet.find((one) => one.kind === "book");
}

/**
 * The fields the content step asks for, in the template's order.
 *
 *   · `book` is the order-book panel, drawn on its own.
 *   · `api` — the person's own order system or payment provider — is not built (#4, #126). It is
 *     one "coming soon" line, not a form of disabled controls.
 *   · A second upload (item names in other languages) is not asked for a template that speaks one
 *     language: there are no other languages, and the build does not read it yet.
 */
export function contentFieldOf(card: AgentTemplateCardView, value: Readonly<Record<string, string>> = {}): readonly AgentTemplateField[] {
  const oneLanguage = speaksOneLanguage(card);
  let uploadSeen = false;
  return card.fieldSet.filter((one) => {
    if (one.when !== undefined) {
      const controlling = card.fieldSet.find((field) => field.code === one.when?.field);
      const selected = value[one.when.field] ?? (controlling?.kind === "choice" ? controlling.defaultValue : "");
      if (selected !== one.when.value) return false;
    }
    if (one.kind === "book" || one.kind === "api") return false;
    if (one.kind === "upload") {
      const keep = !(oneLanguage && uploadSeen);
      uploadSeen = true;
      return keep;
    }
    return true;
  });
}

/**
 * The first upload is the document the agent is built from, so it is required — unless it is one of a required group's
 * alternatives (plan-018: the information may be typed, attached or read from a website), when the group answers for it.
 */
function primaryUploadOf(card: AgentTemplateCardView): AgentTemplateField | undefined {
  return card.fieldSet.find((one) => one.kind === "upload" && one.requiredGroup === undefined);
}

// ── uploads that take several files (plan-018 WAPP-A) ──────────────────────────────────────────

type UploadField = Extract<AgentTemplateField, { kind: "upload" }>;

/** How many files an upload takes: its `maxFileCount`, one when it states none. */
export function fileLimitOf(field: UploadField): number {
  return field.maxFileCount ?? 1;
}

export function takesSeveralFiles(field: AgentTemplateField): boolean {
  return field.kind === "upload" && fileLimitOf(field) > 1;
}

/** The file names an upload's value names: one per line (the api's convention), blank lines ignored. */
export function namedFileOf(value: string | undefined): readonly string[] {
  if (value === undefined) return [];
  return value.split("\n").map((one) => one.trim()).filter((one) => one !== "");
}

/**
 * Where one picked file is kept — in the wizard's `file` record, the stored draft's files and the upload's progress: the
 * field code for a one-file upload (as before), `<code>/<file name>` for each file of an upload that takes several.
 */
export function fileSlotOf(field: AgentTemplateField, name: string): string {
  return takesSeveralFiles(field) ? `${field.code}/${name}` : field.code;
}

/** Every file slot the answer names, in field-set order: what the draft keeps and the build uploads. */
export function fileSlotsOf(
  fieldSet: readonly AgentTemplateField[],
  fieldValue: Readonly<Record<string, string>>,
): readonly { readonly field: UploadField; readonly name: string; readonly slot: string }[] {
  return fieldSet.flatMap((field) => {
    if (field.kind !== "upload") return [];
    if (!takesSeveralFiles(field)) {
      const name = fieldValue[field.code] ?? "";
      return name === "" ? [] : [{ field, name, slot: field.code }];
    }
    return namedFileOf(fieldValue[field.code]).map((name) => ({ field, name, slot: fileSlotOf(field, name) }));
  });
}

export function hasComingSoonIntegration(card: AgentTemplateCardView): boolean {
  return card.fieldSet.some((one) => one.kind === "api");
}

// ── the concurrency choices ─────────────────────────────────────────────────────────────────

/** The prototype's choices (`concOptions: [3, 5, 10, 25, 50]`). */
const CHANNEL_UNIT_CHOICE = [3, 5, 10, 25, 50] as const;

/**
 * The choices the account's tier allows — never one it does not (#17): the prototype's values up to
 * the ceiling, and the ceiling itself when it is not one of them.
 */
export function channelUnitOptionOf(ceiling: number): readonly number[] {
  if (ceiling < 1) return [];
  const within = CHANNEL_UNIT_CHOICE.filter((count) => count < ceiling);
  return [...within, ceiling].filter((count, index, all) => all.indexOf(count) === index);
}

// ── language and voice ─────────────────────────────────────────────────────────────────────

/** "Greek", "Greek and English", "Greek, English and Italian". */
export function inWords(word: readonly string[]): string {
  if (word.length <= 1) return word.join("");
  return portalText("operations", "value_and_value_dcf74a8c", { v0: String(word.slice(0, -1).join(", ")), v1: String(String(word.at(-1))) });
}

export function languageNameOf(pack: readonly LanguagePackView[], code: string): string {
  return pack.find((one) => one.code === code)?.name ?? code;
}

/** The voices that speak every chosen language (#13). None while no language is chosen. */
export function offeredVoiceOf(voice: readonly VoiceView[], languageCode: readonly string[]): readonly VoiceView[] {
  if (languageCode.length === 0) return [];
  return voice.filter(
    (one) => one.supportedLanguageCode.length > 0 && languageCode.every((code) => one.supportedLanguageCode.includes(code)),
  );
}

// ── the context every rule reads ────────────────────────────────────────────────────────────

export interface WizardContext {
  readonly card: AgentTemplateCardView;
  readonly answer: WizardAnswer;
  /** The picked files, by upload field. */
  readonly file: Readonly<Record<string, File>>;
  /** A VoiceAgent a first Build created; its confirmed uploads count as attached. */
  readonly created: CreatedInDraft | null;
  readonly languagePack: readonly LanguagePackView[];
  readonly voice: readonly VoiceView[];
  /** The account's country's language, when it is finished (`GET /v1/languages/suggestion`). */
  readonly suggestedLanguage: string | null;
  /** The account's tier's channel-unit ceiling. */
  readonly ceiling: number;
  /** Whether new numbers can be issued (`GET /v1/numbers/availability`). */
  readonly newNumberAvailable: boolean;
  /** The business, as the account is named. */
  readonly accountName: string;
  /** The other agents' names in this workspace, for the duplicate warning (#41). */
  readonly otherName: readonly string[];
  /**
   * plan-015: whether forwarding to a phone can be chosen, when the API tells this surface; absent or `null` reads as
   * closed (no route serves the switch before a VoiceAgent exists — reported to the integrator).
   */
  readonly forwardFact?: ForwardFact | null;
}

/** What applies right now: the person's answers, and the defaults where there is none yet. */
export interface Resolved {
  readonly languageCode: readonly string[];
  /** One row per chosen language, the greeting language first: the voices that speak it and the one chosen. */
  readonly voiceRow: readonly LanguageVoiceRow[];
  /** Language → the voice chosen for it, for every chosen language some voice speaks (`voice_by_language`). */
  readonly voiceByLanguage: Readonly<Record<string, string>>;
  /** The greeting language's voice (the version's `voice_id`); `""` while no voice speaks it. */
  readonly voiceId: string;
  readonly greetingSuggestion: string | null;
  /** What the agent will say: what was typed, or the suggestion. */
  readonly greeting: string;
  readonly channelUnitOption: readonly number[];
  readonly channelUnitCount: number;
  readonly numberChoice: NumberChoice;
  readonly suggestedName: string;
  readonly displayName: string;
}

function isDuplicate(name: string, otherName: readonly string[]): boolean {
  const wanted = name.trim().toLocaleLowerCase();
  return wanted !== "" && otherName.some((one) => one.trim().toLocaleLowerCase() === wanted);
}

/**
 * The name the Review step starts with, from the person's own business (#21, #128): "Roma Pizzeria
 * orders" for a template that takes orders, "Roma Pizzeria survey" otherwise — or "… (2)" when
 * another agent in the workspace already has it (#41). Never the prototype's "Roma orders".
 */
export function suggestedNameOf(accountName: string, card: AgentTemplateCardView, otherName: readonly string[]): string {
  const named = accountName.trim();
  const what = orderBookFieldOf(card) === undefined ? card.name.toLocaleLowerCase() : "orders";
  return unusedNameOf(named === "" ? card.name : `${named} ${what}`, otherName);
}

/** `stem` when no other agent has it, else the first free "stem (2)", "stem (3)"… */
function unusedNameOf(stem: string, otherName: readonly string[]): string {
  if (!isDuplicate(stem, otherName)) return stem;
  for (let at = 2; ; at += 1) {
    const next = `${stem} (${String(at)})`;
    if (!isDuplicate(next, otherName)) return next;
  }
}

export function resolve(context: WizardContext): Resolved {
  const { answer, card } = context;
  const suggested =
    context.suggestedLanguage !== null && context.languagePack.some((one) => one.code === context.suggestedLanguage)
      ? [context.suggestedLanguage]
      : [];
  const languageCode = answer.languageCode ?? suggested;
  const voiceRow = languageVoiceOf(context, languageCode);
  const voiceByLanguage = Object.fromEntries(voiceRow.filter((one) => one.voiceId !== "").map((one) => [one.languageCode, one.voiceId]));
  const greetingSuggestion = suggestedGreeting({
    agentTemplateCode: card.code,
    hasOrderBook: orderBookFieldOf(card) !== undefined,
    languageCode: languageCode[0],
    accountName: context.accountName,
  });
  const channelUnitOption = channelUnitOptionOf(context.ceiling);
  const channelUnitCount =
    answer.channelUnitCount !== null && channelUnitOption.includes(answer.channelUnitCount)
      ? answer.channelUnitCount
      : (channelUnitOption[0] ?? 0);
  const suggestedName = suggestedNameOf(context.accountName, card, context.otherName);
  return {
    languageCode,
    voiceRow,
    voiceByLanguage,
    voiceId: voiceRow[0]?.voiceId ?? "",
    greetingSuggestion,
    greeting:
      answer.greetingEdited === true || answer.greeting.trim() !== "" ? answer.greeting.trim() : (greetingSuggestion ?? ""),
    channelUnitOption,
    channelUnitCount,
    numberChoice: context.newNumberAvailable ? answer.numberChoice : "bring_my_own",
    suggestedName,
    displayName: answer.displayName ?? suggestedName,
  };
}

// ── what each step still needs ─────────────────────────────────────────────────────────────

/** Keyed by the control a problem belongs to: a field code, or one of these. */
export const PROBLEM_KEY = {
  payment: "payment",
  language: "language",
  voice: "voice",
  greeting: "greeting",
  medium: "medium",
  channelUnit: "channel_unit",
  displayName: "display_name",
  answering: "answering",
} as const;

/** Whether one file slot has its file: picked now, or already confirmed for the agent created. */
export function isSlotAttached(context: WizardContext, slot: string): boolean {
  return (context.file[slot]?.size ?? 0) > 0 || (context.created?.confirmedField.includes(slot) ?? false);
}

/** The names of an upload that takes several files whose file is here (picked now or confirmed). */
export function attachedNameOf(context: WizardContext, field: UploadField): readonly string[] {
  return namedFileOf(context.answer.fieldValue[field.code]).filter((name) => isSlotAttached(context, fileSlotOf(field, name)));
}

/**
 * Whether an upload field has its file: picked now, or already confirmed for the agent created. An upload that takes
 * several files has them when it names at least one and every one it names is here.
 */
export function isAttached(context: WizardContext, fieldCode: string): boolean {
  const field = context.card.fieldSet.find((one) => one.code === fieldCode);
  if (field?.kind === "upload" && takesSeveralFiles(field)) {
    const named = namedFileOf(context.answer.fieldValue[field.code]);
    return named.length > 0 && attachedNameOf(context, field).length === named.length;
  }
  return isSlotAttached(context, fieldCode);
}

/** Whether a field counts as given for its required group — the api's rule, from what the wizard holds. */
function isGivenForGroup(context: WizardContext, field: AgentTemplateField): boolean {
  if (field.code === BI_CONFIGURATION_FIELD) {
    try { return biConfigurationOf(context.answer.fieldValue[field.code] ?? "").sources.length > 0; } catch { return false; }
  }
  if (field.kind === "upload") {
    return takesSeveralFiles(field) ? attachedNameOf(context, field).length > 0 : isAttached(context, field.code) && !isOverTypedLimit(field, context.file[field.code]);
  }
  if (field.kind === "website") return websiteAddressSentOf(context.answer.fieldValue[field.code] ?? "") !== null;
  if (field.kind === "choice") return (context.answer.fieldValue[field.code] ?? field.defaultValue) !== "";
  return (context.answer.fieldValue[field.code] ?? "").trim() !== "";
}

// ── what the help assistant is told about the wizard (owner request 2026-10-17) ─────────────────

/** A step's code in the help page registry (`agent_setup`'s places): Content (or Menu · Hours · Orders), Languages, … */
export function wizardHelpPlaceOf(def: WizardStepDef): string {
  return def.kind === "content" ? (def.part === "all" ? "content" : def.part) : def.kind;
}

/** The help registry's state code a content field reports under, by its kind. */
function helpStateCodeOf(field: AgentTemplateField): string {
  if (field.kind === "website") return "website_address";
  if (field.kind === "upload") return "document";
  if (field.kind === "choice") return "choice";
  if (field.kind === "text" && fieldFormatOf(field) === "opening_hours") return "opening_hours";
  return "typed_information";
}

/**
 * Which of the step's content fields are empty or given, by kind (a kind is given when any of its fields is), and
 * whether the step can be left with Continue — never a value: the help assistant learns "the website address is
 * empty", never what was typed or attached.
 */
export function wizardHelpStateOf(
  context: WizardContext,
  field: readonly AgentTemplateField[],
  stepComplete: boolean,
): readonly { readonly code: string; readonly filled: boolean }[] {
  const byCode = new Map<string, boolean>();
  for (const one of field) {
    const code = helpStateCodeOf(one);
    const given = code === "opening_hours" ? context.answer.openingWeek[one.code] !== undefined : isGivenForGroup(context, one);
    byCode.set(code, (byCode.get(code) ?? false) || given);
  }
  return [...[...byCode].map(([code, filled]) => ({ code, filled })), { code: "step_complete", filled: stepComplete }];
}

/** Give separate questionnaire uploads distinct names even when both were called questions.txt. */
export function uploadFileOf(field: Extract<AgentTemplateField, { kind: "upload" }>, file: File): File {
  return field.allowText === true ? new File([file], `${field.code}--${file.name}`, { type: file.type }) : file;
}

function contentProblem(context: WizardContext): Record<string, string> {
  const problem: Record<string, string> = {};
  const primary = primaryUploadOf(context.card);
  const shown = contentFieldOf(context.card, context.answer.fieldValue);
  for (const field of shown) {
    if (field.kind === "website") {
      const found = websiteProblemOf(context.answer.fieldValue[field.code] ?? "");
      if (found !== null) problem[field.code] = found;
      continue;
    }
    if (field.kind === "upload" && isOverTypedLimit(field, context.file[field.code])) {
      problem[field.code] = typedTooLargeText();
      continue;
    }
    if (field.kind === "upload" && takesSeveralFiles(field)) {
      // A name kept across a reload whose file the browser did not keep: said, so it is attached again or removed.
      const named = namedFileOf(context.answer.fieldValue[field.code]);
      const lost = named.filter((name) => !attachedNameOf(context, field).includes(name));
      if (lost.length > 0) {
        problem[field.code] = portalText("operations", "value_was_attached_before_the_page_reloaded_and_a_browser_56c85adb", { v0: lost.join(", ") });
      } else if (named.length === 0 && (field === primary || field.required === true)) {
        problem[field.code] = portalText("operations", "attach_a_file_here_the_agent_is_built_from_it_6d942cc7");
      }
      continue;
    }
    if (field.kind === "choice") {
      const selected = context.answer.fieldValue[field.code] ?? field.defaultValue;
      if (!field.options.some((option) => option.value === selected)) problem[field.code] = portalText("operations", "choose_one_of_these_options_4f96f3d1");
      continue;
    }
    if (field.kind === "upload") {
      if (isAttached(context, field.code)) continue;
      const name = context.answer.fieldValue[field.code];
      const named = name !== undefined && name !== "";
      // A group's alternative is not required on its own — but a name whose file was lost (a reload) is still said; an
      // emptied typed box is simply not given.
      const lost = named && field.requiredGroup !== undefined && context.file[field.code] === undefined;
      if (field !== primary && field.required !== true && !lost) continue;
      problem[field.code] = !named
        ? field.allowText === true ? typedUploadCopyOf(field).missing : portalText("operations", "attach_a_file_here_the_agent_is_built_from_it_6d942cc7")
        : portalText("operations", "value_was_attached_before_the_page_reloaded_and_a_browser_56c85adb", { v0: String(name) });
      continue;
    }
    if (field.kind !== "text") continue;
    if (field.code === BI_CONFIGURATION_FIELD) {
      try {
        const value = context.answer.fieldValue[field.code] ?? "";
        if (value.length > field.maxLength) throw new Error("limit");
        const config = biConfigurationOf(value);
        const names = [...namedFileOf(context.answer.fieldValue["bi_documents"]), ...biDatasetChoices(config.sources).map((source) => source.id)];
        for (const source of config.sources) if ("tables" in source && biSelectedDatasets(source).length === 0) throw new Error("no_tables");
        if (config.sources.length + namedFileOf(context.answer.fieldValue["bi_documents"]).length > BI_LIMIT.sources || names.length > BI_LIMIT.datasets || new Set(names).size !== names.length) throw new Error("duplicate_or_excess_sources");
        if (config.relationships.some((relationship) => !names.includes(relationship.from) || !names.includes(relationship.to) || relationship.from === relationship.to)) throw new Error("unknown_dataset");
      } catch { problem[field.code] = portalText("operations", "bi_configuration_invalid"); }
      continue;
    }
    const format = fieldFormatOf(field);
    if (format === "opening_hours") {
      const week = context.answer.openingWeek[field.code] ?? closedWeek();
      const found = openingWeekProblem(week);
      if (found !== null) problem[field.code] = found;
      else if (openingHoursText(week).length > field.maxLength) {
        problem[field.code] = portalText("operations", "these_hours_are_too_detailed_to_save_give_more_days_the_sa_df4ea049");
      }
      // plan-015: the choices drawn under the week — a forward while forwarding is closed, or a refused number.
      const choiceProblem = answeringChoiceProblemOf(
        context.answer.answeringChoice ?? DEFAULT_ANSWERING_CHOICE,
        context.forwardFact ?? null,
      );
      if (choiceProblem !== null) problem[PROBLEM_KEY.answering] = choiceProblem;
      continue;
    }
    const value = (context.answer.fieldValue[field.code] ?? "").trim();
    if (value === "") {
      // One of a required group's alternatives may be left empty; the group answers for it.
      if (field.requiredGroup !== undefined) continue;
      problem[field.code] =
        format === "amount"
          ? portalText("operations", "write_an_amount_for_example_2_50_or_0_if_delivery_is_free_846b0352")
          : format === "amount_or_none"
            ? portalText("operations", "write_an_amount_for_example_25_00_or_none_200965ec")
            : portalText("operations", "fill_this_in_the_build_needs_it_8873a8da");
      continue;
    }
    if (format !== null) {
      const found = amountProblem(value, format);
      if (found !== null) problem[field.code] = found;
    }
  }
  const missing = missingRequiredGroupOf(shown, (field) => isGivenForGroup(context, field));
  if (missing !== null) {
    problem[requiredGroupKeyOf(missing.group)] = requiredGroupTextOf(shown.filter((one) => one.requiredGroup === missing.group));
  }
  if (orderBookFieldOf(context.card) !== undefined && context.answer.paymentMethod === null) {
    problem[PROBLEM_KEY.payment] = portalText("operations", "choose_how_callers_pay_for_their_order_d589b263");
  }
  return problem;
}

/**
 * What the Languages step still needs: a language; one only for a template that speaks one; a voice for every chosen
 * language (each language has its own, plan-015 G1). The greeting is the Review step's since 2026-10-10.
 */
function languageProblem(context: WizardContext, resolved: Resolved): Record<string, string> {
  const problem: Record<string, string> = {};
  const oneLanguage = speaksOneLanguage(context.card);
  const names = resolved.languageCode.map((code) => languageNameOf(context.languagePack, code));
  if (resolved.languageCode.length === 0) {
    problem[PROBLEM_KEY.language] = oneLanguage
      ? portalText("operations", "choose_the_language_the_agent_speaks_the_language_your_men_23d6ce62")
      : portalText("operations", "choose_at_least_one_language_for_the_agent_to_speak_34200767");
  } else if (oneLanguage && resolved.languageCode.length > 1) {
    problem[PROBLEM_KEY.language] =
      portalText("operations", "value_agents_speak_one_language_for_now_the_language_your_c2b2e882", { v0: String(context.card.name) }) +
      portalText("operations", "choose_one_of_value_e9f72586", { v0: String(inWords(names)) });
  } else {
    const silent = resolved.voiceRow.filter((one) => one.voiceId === "");
    if (silent.length > 0) {
      problem[PROBLEM_KEY.voice] = portalText("operations", "no_voice_speaks_value_yet_so_an_agent_cannot_answer_in_it_4fc01f2c", { v0: String(inWords(silent.map((one) => one.languageName))) });
    }
  }

  return problem;
}

function mediumProblem(context: WizardContext, resolved: Resolved): Record<string, string> {
  const problem: Record<string, string> = {};
  if (context.answer.medium.length === 0) problem[PROBLEM_KEY.medium] = portalText("operations", "choose_at_least_one_channel_c8ad2196");
  if (resolved.channelUnitOption.length === 0) {
    problem[PROBLEM_KEY.channelUnit] = portalText("operations", "your_organization_allows_no_conversations_at_once_ask_for_8f43f113");
  }
  return problem;
}

/**
 * What the Review step still needs: a name, and a greeting — asked here, beside the name, since the export of 2026-10-10
 * took the greeting box off the Languages step (a language with no suggestion has no greeting until it is written).
 */
function summaryProblem(resolved: Resolved): Record<string, string> {
  return {
    ...(resolved.displayName.trim() === "" ? { [PROBLEM_KEY.displayName]: portalText("operations", "give_the_agent_a_name_d95c3597") } : {}),
    ...(resolved.greeting === "" ? { [PROBLEM_KEY.greeting]: portalText("operations", "write_the_greeting_callers_hear_when_the_agent_answers_64ad0168") } : {}),
  };

}

export function problemOf(step: number, context: WizardContext, resolved: Resolved): Readonly<Record<string, string>> {
  if (step === CONTENT_STEP) return contentProblem(context);
  if (step === LANGUAGE_STEP) return languageProblem(context, resolved);
  if (step === MEDIUM_STEP) return mediumProblem(context, resolved);
  return summaryProblem(resolved);
}

/** The first step with something missing; the Review step when nothing is. */
export function firstIncompleteStep(context: WizardContext, resolved: Resolved): number {
  for (const step of [CONTENT_STEP, LANGUAGE_STEP, MEDIUM_STEP]) {
    if (Object.keys(problemOf(step, context, resolved)).length > 0) return step;
  }
  return SUMMARY_STEP;
}

/** The warning under the name — a warning, not a refusal: two agents may share a name (#41). */
export function duplicateNameWarning(context: WizardContext, resolved: Resolved): string | null {
  if (!isDuplicate(resolved.displayName, context.otherName)) return null;
  const name = resolved.displayName.trim();
  return portalText("operations", "another_agent_in_this_workspace_is_already_called_value_a_796af1cc", { v0: String(name), v1: String(unusedNameOf(name, context.otherName)) });
}

// ── the request ────────────────────────────────────────────────────────────────────────────

/**
 * What "Build the agent" sends. Call it only when {@link firstIncompleteStep} is the Review step. `answering` is
 * {@link answeringOf}'s, sent for a template that asks its opening hours (every template that answers callers).
 */
export function buildRequestOf(
  context: WizardContext,
  resolved: Resolved,
  answering: WizardAnswering | null = null,
): VoiceAgentDraft {
  const { card, answer } = context;
  const fieldValue: Record<string, string> = {};
  for (const field of contentFieldOf(card, answer.fieldValue)) {
    if (field.kind === "choice") {
      fieldValue[field.code] = answer.fieldValue[field.code] ?? field.defaultValue;
      continue;
    }
    if (field.kind === "website") {
      const sent = websiteAddressSentOf(answer.fieldValue[field.code] ?? "");
      if (sent !== null) fieldValue[field.code] = sent;
      continue;
    }
    if (field.kind === "upload" && takesSeveralFiles(field)) {
      const attached = attachedNameOf(context, field);
      if (attached.length > 0) fieldValue[field.code] = attached.join("\n");
      continue;
    }
    if (field.kind === "upload") {
      const name = answer.fieldValue[field.code];
      const picked = context.file[field.code];
      if (name !== undefined && name !== "" && isAttached(context, field.code)) {
        fieldValue[field.code] = picked === undefined ? (field.allowText === true ? `${field.code}--${name}` : name) : uploadFileOf(field, picked).name;
      }
      continue;
    }
    if (field.kind !== "text") continue;
    if (fieldFormatOf(field) === "opening_hours") {
      fieldValue[field.code] = openingHoursText(answer.openingWeek[field.code] ?? closedWeek());
      continue;
    }
    fieldValue[field.code] = (answer.fieldValue[field.code] ?? "").trim();
  }
  const orderBook = orderBookFieldOf(card);
  // The only destination that works today; recorded so the build input says so in words.
  if (orderBook !== undefined) fieldValue[orderBook.code] = "book";
  return {
    agentTemplateCode: card.code,
    displayName: resolved.displayName.trim(),
    fieldValue,
    languageCode: resolved.languageCode,
    voiceId: resolved.voiceId,
    voiceByLanguage: resolved.voiceByLanguage,
    greeting: resolved.greeting,
    numberChoice: resolved.numberChoice,
    medium: answer.medium,
    channelUnitCount: resolved.channelUnitCount,
    recordingEnabled: answer.recordingEnabled,
    ...(orderBook === undefined ? {} : { paymentMethod: answer.paymentMethod ?? [] }),
    ...(answering === null || !asksOpeningHours(card) ? {} : { answering: answeringRequestOf(answering) }),
  };
}

// ── plan-015 AGC-UI: the answering answers (G4, G8, G12) ─────────────────────────────────────

/** Whether the template asks its opening hours — every template that answers callers does (the catalogue's rule). */
export function asksOpeningHours(card: AgentTemplateCardView): boolean {
  return card.fieldSet.some((one) => fieldFormatOf(one) === "opening_hours");
}

/** The answering answers that apply now: the person's, and the defaults where there is none yet. */
export interface WizardAnswering {
  /** The VoiceAgent's own zone: the person's choice, else the workspace's; `null` while neither is known. */
  readonly timeZone: string | null;
  readonly specialDate: readonly SpecialDateEntry[];
  /** Say you are closed, hold callers for two minutes — until the person chooses. */
  readonly choice: AnsweringChoice;
}

export function answeringOf(answer: WizardAnswer, workspaceTimeZone: string | null): WizardAnswering {
  return {
    timeZone: answer.timeZone ?? workspaceTimeZone,
    specialDate: answer.specialDate ?? [],
    choice: answer.answeringChoice ?? DEFAULT_ANSWERING_CHOICE,
  };
}

/**
 * The request's `answering`: the zone (left out while unknown — the API then writes the workspace's), the special dates
 * and the choices, always stated, so the busy sentence the wizard shows is the wait the build uses (G8).
 */
export function answeringRequestOf(answering: WizardAnswering): AnsweringRequestView {
  return {
    ...(answering.timeZone === null ? {} : { timeZone: answering.timeZone }),
    specialDate: answering.specialDate,
    choice: sentChoiceOf(answering.choice),
  };
}

// ── design sync 2026-10-09: the step list, per template ─────────────────────────────────────

/**
 * What a step asks. `content` steps carry the PART of the template's fields they ask: everything, or — for a template
 * with an order book — the menu (its uploads), the hours (its opening-hours field) and the orders (the order book, how
 * customers pay and every other field). Decided by the field kinds, never by the template's code (F-FAC-1).
 */
export type WizardStepKind = "content" | "language" | "medium" | "summary";
export type ContentPart = "all" | "menu" | "hours" | "orders";

export interface WizardStepDef {
  /** The number the step list shows and `?step=` carries; the picker is 1. */
  readonly step: number;
  readonly label: string;
  readonly kind: WizardStepKind;
  readonly part: ContentPart;
}

/** A template with an order book splits its content step into Menu, Hours and Orders. */
export function splitsContent(card: AgentTemplateCardView): boolean {
  return orderBookFieldOf(card) !== undefined;
}

/** Which part of a split content step a field belongs to (a website, like a document, is what the agent reads). */
export function contentPartOf(field: AgentTemplateField): Exclude<ContentPart, "all"> {
  if (field.kind === "upload" || field.kind === "website") return "menu";
  if (field.kind === "text" && fieldFormatOf(field) === "opening_hours") return "hours";
  return "orders";
}

function contentPartLabelOf(part: Exclude<ContentPart, "all">): string {
  if (part === "menu") return portalText("operations", "menu_57f5f5ef");
  if (part === "hours") return portalText("operations", "hours_9e25a34e");
  return portalText("operations", "orders_cded0933");
}

/** The steps after the picker: 2 Content (or 2 Menu, 3 Hours, 4 Orders), then Languages, Channels, Review. */
export function wizardStepsOf(card: AgentTemplateCardView): readonly WizardStepDef[] {
  const content: { readonly label: string; readonly part: ContentPart }[] = splitsContent(card)
    ? (["menu", "hours", "orders"] as const)
        .filter((part) => part === "orders" || card.fieldSet.some((field) => field.kind !== "book" && field.kind !== "api" && contentPartOf(field) === part))
        .map((part) => ({ label: contentPartLabelOf(part), part }))
    : [{ label: portalText("operations", "content_4f9be057"), part: "all" }];
  const row: { readonly label: string; readonly kind: WizardStepKind; readonly part: ContentPart }[] = [
    ...content.map((one) => ({ ...one, kind: "content" as const })),
    { label: portalText("operations", "languages_db07be18"), kind: "language", part: "all" },
    { label: portalText("operations", "channels_18e03e2a"), kind: "medium", part: "all" },
    { label: portalText("operations", "review_e29a79fe"), kind: "summary", part: "all" },
  ];
  return row.map((one, index) => ({ ...one, step: CONTENT_STEP + index }));
}

/** The content fields one step asks for. */
export function contentFieldOfPart(
  card: AgentTemplateCardView,
  part: ContentPart,
  value: Readonly<Record<string, string>> = {},
): readonly AgentTemplateField[] {
  const field = contentFieldOf(card, value);
  return part === "all" ? field : field.filter((one) => contentPartOf(one) === part);
}

/**
 * A content field's row title on the Review step: its label without a trailing parenthetical, so the delivery menu's
 * "Menu file (required)" (the export's label on the Content step, plan-015 G7) reads "Menu file" there.
 */
export function summaryTermOf(field: AgentTemplateField): string {
  return field.label.replace(/\s*\([^()]*\)\s*$/u, "");
}

// ── who the agent talks to ──────────────────────────────────────────────────────────────────

export type { ConversationDirection };

/**
 * The picker's tag under a card, from the catalogue's `direction` (plan-015 G7, migration 0118, served by
 * `GET /v1/templates` as `AgentTemplateCardView.direction`) — never inferred from the template's fields.
 */
export function directionTagOf(card: AgentTemplateCardView): string {
  return card.direction === "inbound" ? portalText("operations", "answers_incoming_calls_e3c91d7a") : portalText("operations", "makes_outgoing_calls_a6b764df");
}

// ── how customers pay: three choices (design sync 2026-10-09) ────────────────────────────────

export type PaymentChoice = "link" | "delivery" | "none";

/**
 * The methods each choice sends, one-to-one (plan-015 G3 AC (c)): "Send a checkout link by SMS" is `online` alone (owner
 * answer 2 — the API accepts it; while the account cannot offer online payment the agent takes a message instead and the
 * agent page warns), "Cash or card on delivery" both hand-over methods, "Do not take payment" none.
 */
export const PAYMENT_CHOICE_METHOD: Readonly<Record<PaymentChoice, readonly PaymentMethodCode[]>> = {
  link: ["online"],
  delivery: ["cash_on_delivery", "card_on_delivery"],
  none: [],
};

/** The prototype's default: cash or card on delivery. */
export const DEFAULT_PAYMENT_METHOD: readonly PaymentMethodCode[] = PAYMENT_CHOICE_METHOD.delivery;

/**
 * Which choice an answer is, or `null` for one the three do not name (cash only, card only, or `online` beside a
 * hand-over method — what the link choice sent before plan-015).
 */
export function paymentChoiceOf(method: readonly PaymentMethodCode[] | null): PaymentChoice | null {
  if (method === null) return null;
  if (method.length === 1 && method[0] === "online") return "link";
  if (method.includes("online")) return null;
  if (method.length === 0) return "none";
  return method.length === 2 && method.includes("cash_on_delivery") && method.includes("card_on_delivery") ? "delivery" : null;
}

// ── a voice for each language (design sync 2026-10-09) ───────────────────────────────────────

export interface LanguageVoiceRow {
  readonly languageCode: string;
  readonly languageName: string;
  /** plan-026 (0176): the language's pack is `beta`, so its row carries the Beta badge. */
  readonly beta: boolean;
  /** The voices that speak this language, in the catalogue's order (`GET /v1/voices`, by name). */
  readonly option: readonly VoiceView[];
  /** The voice chosen for it, `""` when no voice speaks it. */
  readonly voiceId: string;
}

/**
 * One row per chosen language, the greeting language first. A language's voice is the person's choice for it; else the
 * greeting language's voice when it speaks this one too; else the agent's voice so far (a shared configuration's); else
 * a voice that speaks every chosen language; else one that speaks this one.
 *
 * AMENDED 2026-10-21 (plan-026, migration 0176): in the last two, the language's default voice
 * (`LanguagePackView.defaultVoiceId` — es Carolina Ruiz, pt Patricio Brito) is taken when it is among those voices,
 * else the first in the catalogue's order (by name) as before. A language with no default (el, en) is unchanged, and a
 * default that does not speak the language is ignored.
 */
export function languageVoiceOf(context: WizardContext, languageCode: readonly string[]): readonly LanguageVoiceRow[] {
  const chosen = context.answer.voiceByLanguage ?? {};
  const speaksAll = offeredVoiceOf(context.voice, languageCode);
  const row: LanguageVoiceRow[] = [];
  for (const code of languageCode) {
    const option = context.voice.filter((one) => one.supportedLanguageCode.includes(code));
    const offers = (voiceId: string | undefined): voiceId is string => voiceId !== undefined && voiceId !== "" && option.some((one) => one.voiceId === voiceId);
    const primary = row[0]?.voiceId;
    const defaultVoiceId = context.languagePack.find((one) => one.code === code)?.defaultVoiceId ?? null;
    /** The language's default voice when `among` holds it, else the first of `among`. */
    const preferredOf = (among: readonly VoiceView[]): string | undefined =>
      (among.find((one) => one.voiceId === defaultVoiceId) ?? among[0])?.voiceId;
    const candidate = [chosen[code], primary, context.answer.voiceId, preferredOf(speaksAll), preferredOf(option)];
    row.push({
      languageCode: code,
      languageName: languageNameOf(context.languagePack, code),
      beta: isBetaLanguage(context.languagePack, code),
      option,
      voiceId: candidate.find(offers) ?? "",
    });
  }
  return row;
}

/** What a step still needs, in words, keyed by the control it belongs to. */
export function wizardStepProblemOf(def: WizardStepDef, context: WizardContext, resolved: Resolved): Readonly<Record<string, string>> {
  if (def.kind === "content") {
    const all = problemOf(CONTENT_STEP, context, resolved);
    const field = contentFieldOfPart(context.card, def.part, context.answer.fieldValue);
    const asked = new Set(field.map((one) => one.code));
    for (const one of field) if (one.requiredGroup !== undefined) asked.add(requiredGroupKeyOf(one.requiredGroup));
    if (def.part === "all" || def.part === "orders") asked.add(PROBLEM_KEY.payment);
    if ((def.part === "all" || def.part === "hours") && asksOpeningHours(context.card)) asked.add(PROBLEM_KEY.answering);
    return Object.fromEntries(Object.entries(all).filter(([key]) => asked.has(key)));
  }
  if (def.kind === "language") return problemOf(LANGUAGE_STEP, context, resolved);
  if (def.kind === "medium") return problemOf(MEDIUM_STEP, context, resolved);
  return problemOf(SUMMARY_STEP, context, resolved);
}

/**
 * The sentence beside a disabled Continue (the prototype's `nextHint`): the document is not attached, no language is
 * chosen, or the agent has no name. Every other problem is said under its control when Continue is pressed.
 */
export function wizardNextHintOf(def: WizardStepDef, context: WizardContext, resolved: Resolved): string | null {
  if (def.kind === "content" && (def.part === "all" || def.part === "menu")) {
    const primary = primaryUploadOf(context.card);
    if (primary !== undefined && !isAttached(context, primary.code)) {
      return def.part === "menu" ? portalText("operations", "attach_the_menu_file_to_continue_29f07b20") : portalText("operations", "attach_the_file_to_continue_9f8c3769");
    }
  }
  if (def.kind === "language" && resolved.languageCode.length === 0) return portalText("operations", "pick_at_least_one_language_afad7b7b");
  if (def.kind === "summary" && resolved.displayName.trim() === "") return portalText("operations", "give_the_agent_a_name_d95c3597");
  return null;
}

/** The first step with something missing; the Review step when nothing is. */
export function firstIncompleteWizardStep(steps: readonly WizardStepDef[], context: WizardContext, resolved: Resolved): number {
  for (const def of steps) {
    if (def.kind === "summary") return def.step;
    if (Object.keys(wizardStepProblemOf(def, context, resolved)).length > 0 || wizardNextHintOf(def, context, resolved) !== null) {
      return def.step;
    }
  }
  return steps.at(-1)?.step ?? SUMMARY_STEP;
}

// ── capacity: the prototype's five choices, the ones above the organization's limit disabled ─

export interface ChannelUnitChoice {
  readonly count: number;
  /** Above the organization's limit: shown, and disabled. */
  readonly over: boolean;
}

/** `3, 5, 10, 25, 50` with the ceiling itself among them when it is not one, those above it disabled. */
export function channelUnitChoiceOf(ceiling: number): readonly ChannelUnitChoice[] {
  const count = [...CHANNEL_UNIT_CHOICE] as number[];
  if (ceiling >= 1 && ceiling < 50 && !count.includes(ceiling)) count.push(ceiling);
  return count.sort((one, other) => one - other).map((one) => ({ count: one, over: one > ceiling }));
}
